import { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";

/**
 * Ortak kamera + QR okuyucu (jsQR). Giriş-çıkış kioski ve sevkiyat karekod ekranı kullanır.
 * Kamera `enabled` iken açılır, kapanınca (ya da bileşen sökülünce) akış durdurulur.
 * Aynı kod `dedupeMs` içinde ikinci kez bildirilmez; `onCode` false dönerse okuma sayılmaz
 * (ör. meşgulken atlanan kod bir sonraki karede yeniden okunur).
 */
export type CameraState = "idle" | "on" | "denied" | "none";

export interface QrScannerOptions {
  enabled: boolean;
  /** "user" ön kamera (kiosk tableti), "environment" arka kamera (telefonla okutma). */
  facingMode?: "user" | "environment";
  width?: number;
  height?: number;
  /** Kod bu süzgeçten geçmezse yok sayılır (ör. kiosk yalnız "nq_" ile başlayanları alır). */
  accept?: (data: string) => boolean;
  dedupeMs?: number;
  onCode: (data: string) => boolean | void;
}

export function useQrScanner({ enabled, facingMode = "user", width = 640, height = 480, accept, dedupeMs = 5000, onCode }: QrScannerOptions) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [camera, setCamera] = useState<CameraState>("idle");
  const lastRef = useRef<{ code: string; at: number }>({ code: "", at: 0 });
  // Geri çağrılar ref'te: her render'da kamera yeniden başlatılmasın
  const onCodeRef = useRef(onCode);
  const acceptRef = useRef(accept);
  onCodeRef.current = onCode;
  acceptRef.current = accept;

  useEffect(() => {
    if (!enabled) { setCamera("idle"); return; }
    let raf = 0; let stopped = false; let stream: MediaStream | null = null;
    const video = videoRef.current;
    const start = async () => {
      if (!navigator.mediaDevices?.getUserMedia) { setCamera("none"); return; }
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode, width: { ideal: width }, height: { ideal: height } } });
        if (stopped) { stream.getTracks().forEach((t) => t.stop()); return; }
        const v = videoRef.current; const c = canvasRef.current;
        if (!v || !c) { stream.getTracks().forEach((t) => t.stop()); return; }
        v.srcObject = stream; await v.play(); setCamera("on");
        const ctx = c.getContext("2d", { willReadFrequently: true })!;
        const tick = () => {
          if (stopped) return;
          if (v.readyState === v.HAVE_ENOUGH_DATA) {
            c.width = v.videoWidth; c.height = v.videoHeight; ctx.drawImage(v, 0, 0, c.width, c.height);
            const img = ctx.getImageData(0, 0, c.width, c.height);
            const code = jsQR(img.data, img.width, img.height, { inversionAttempts: "dontInvert" });
            if (code?.data && (!acceptRef.current || acceptRef.current(code.data))) {
              const t = Date.now();
              if (code.data !== lastRef.current.code || t - lastRef.current.at > dedupeMs) {
                if (onCodeRef.current(code.data) !== false) lastRef.current = { code: code.data, at: t };
              }
            }
          }
          raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      } catch (e: any) {
        if (!stopped) setCamera(e?.name === "NotFoundError" || e?.name === "OverconstrainedError" ? "none" : "denied");
      }
    };
    start();
    return () => {
      stopped = true; cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
      if (video) video.srcObject = null;
    };
  }, [enabled, facingMode, width, height, dedupeMs]);

  return { videoRef, canvasRef, camera };
}

/** Okutma geri bildirimi: kısa bip (Web Audio) + titreşim. Tarayıcı izin vermezse sessizce geçer. */
let audioCtx: AudioContext | null = null;
export function scanFeedback(kind: "ok" | "dup" | "err") {
  try {
    const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (AC) {
      audioCtx = audioCtx || new AC();
      const ctx = audioCtx!;
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = kind === "err" ? "square" : "sine";
      o.frequency.value = kind === "ok" ? 1320 : kind === "dup" ? 880 : 220;
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + (kind === "err" ? 0.32 : 0.12));
      o.connect(g); g.connect(ctx.destination);
      o.start(); o.stop(ctx.currentTime + (kind === "err" ? 0.34 : 0.14));
    }
  } catch { /* ses yoksa sorun değil */ }
  try { navigator.vibrate?.(kind === "err" ? [90, 60, 90] : kind === "dup" ? 30 : 60); } catch { /* yok */ }
}
